import { gql, useApolloClient } from '@apollo/client';
import { toBoolean } from '@mnd-frontend/ts';
import {
  Article,
  Button,
  Card,
  CardHeader,
  Checkbox,
  DataTable,
  Dialog,
  InputRow,
  Page,
  PageHeader,
  Pagination,
} from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { localizeValue } from '@mnd-frontend/utils';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useCurrentPressroom } from '../../../../../contexts/CurrentPressroomContext';
import {
  useInactiveNetworkContactsQuery,
  useNetworkInactiveContactRemoveMutation,
} from '../../../../../generated/hooks';
import qs from '../../../../../lib/normalizingQs';
import { track } from '../../../../../lib/tracker';
import getContactName from '../../../../../modules/contacts/getContactName';
import routes from '../../../../../routes';
import ContactListEmpty from '../ContactListEmpty';
import { useGetRows, useGetTableHeadings } from './hooks';

gql`
  query InactiveNetworkContacts($pressroomId: ID!, $page: Int, $first: Int) {
    pressroom(id: $pressroomId) {
      id
      networkInactiveContactsList {
        contactsCount
        name
        contacts2(first: $first, page: $page) {
          nodes {
            id
            ...NetworkContact
            networkContactLists {
              id
            }
          }
          currentPage
          totalPages
          pageInfo {
            hasNextPage
          }
        }
      }
    }
  }

  mutation NetworkInactiveContactRemove($pressroomId: ID!, $ids: [ID!]!, $deleteAll: Boolean) {
    networkInactiveContactRemove(
      input: { pressroomId: $pressroomId, ids: $ids, deleteAll: $deleteAll }
    ) {
      error {
        message
      }
      deletedIds
    }
  }
`;

const ContactsInactivePage = () => {
  const { pressroomId } = useCurrentPressroom();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { showSuccessToast, showFailureToast } = useToasts();
  const apolloClient = useApolloClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const location = useLocation();
  const page = Number.parseInt(qs.parse(location.search).page || '1', 10);

  const [selected, setSelected] = useState<{ value: Set<string> }>({ value: new Set() });
  const [contactNameLookup, setContactNameLookup] = useState<{ [id: string]: string }>({});

  const { data, loading } = useInactiveNetworkContactsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      pressroomId,
      page,
      first: 20,
    },
    onCompleted: response => {
      const newSelected = new Set<string>();
      const newLookup: typeof contactNameLookup = {};
      response?.pressroom?.networkInactiveContactsList?.contacts2?.nodes
        ?.filter(toBoolean)
        ?.forEach(contact => {
          newSelected.add(contact.id);
          newLookup[contact.id] = getContactName(contact, false);
        });
      setContactNameLookup(newLookup);
    },
    onError: () => showFailureToast(),
  });

  const networkContacts = data?.pressroom?.networkInactiveContactsList;
  const contacts = useMemo(
    () => networkContacts?.contacts2?.nodes?.filter(toBoolean) ?? [],
    [networkContacts?.contacts2],
  );

  const totalCount = networkContacts?.contactsCount ?? 0;
  const [allSelected, setAllSelected] = useState(true);
  const headings = useGetTableHeadings();

  const deferredSelected = useDeferredValue(selected);
  const rows = useGetRows(contacts, deferredSelected, setSelected, allSelected);

  const currentPage = networkContacts?.contacts2?.currentPage ?? 0;
  const totalPages = networkContacts?.contacts2?.totalPages ?? 0;

  const [removeNetworkContacts, { loading: loadingRemove }] =
    useNetworkInactiveContactRemoveMutation({
      variables: {
        pressroomId,
        ids: allSelected ? [] : [...selected.value],
        deleteAll: allSelected,
      },
      onCompleted: data => {
        const error = data.networkInactiveContactRemove?.error?.message;
        if (error) {
          showFailureToast(error);
        } else {
          const deletedIds = data.networkInactiveContactRemove?.deletedIds;
          if (deletedIds) {
            deletedIds.forEach(deletedId => {
              apolloClient.cache.evict({
                id: apolloClient.cache.identify({
                  id: deletedId,
                  __typename: 'NetworkContact',
                }),
              });
            });
          }
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({
              id: pressroomId,
              __typename: 'Pressroom',
            }),
            fields: {
              networkInactiveContactsList: (_, { DELETE }) => DELETE,
            },
          });

          const allDeletedForCurrentPage = contacts
            .map(contact => contact.id)
            ?.every(id => deletedIds?.includes(id));

          showSuccessToast(
            t('contacts_toasts_delete-contact-success_plural', {
              count: deletedIds?.length ?? 0,
              contactName: contacts[0]?.firstName + ' ' + contacts[0]?.lastName,
            }),
          );
          setSelected({ value: new Set() });
          setShowDeleteModal(false);
          if (currentPage === totalPages && allDeletedForCurrentPage) {
            if (totalPages === 1) {
              navigate(routes.contacts({ pressroomId }));
            } else {
              handleChangePage(1);
            }
          }
        }
      },
      onError: () => {
        showFailureToast(t('common_general_error'));
      },
    });

  const getTrackingContacts = () =>
    [...selected.value].map(contactId => ({
      contactId,
      name: contactNameLookup[contactId],
    }));

  const handleChangePage = useCallback(
    (page: any) => {
      const search = qs.parse(location.search);
      navigate({
        search: qs.stringify({
          ...search,
          page,
        }),
      });
    },
    [navigate, location.search],
  );

  return (
    <>
      <Page>
        <PageHeader
          title={t('contacts_inactive-title')}
          description={t('contacts_inactive-description')}
          breadcrumb={t('contacts_back-to-list')}
          breadcrumbTo={routes.contacts({
            pressroomId,
          })}
        />
        <Article>
          <Card style={{ marginBottom: '1.5rem' }} overflowVisible>
            <CardHeader style={{ paddingTop: '1.5rem' }} isSticky>
              {totalCount > 0 && (
                <InputRow $justifyContent="start">
                  <Checkbox
                    onChange={e => {
                      track({
                        event: 'Contacts: Checked All Inactive Contacts',
                        traits: { checked: e.target.checked },
                      });
                      if (e.target.checked) {
                        setAllSelected(true);
                      } else {
                        setSelected({ value: new Set() });
                        setAllSelected(false);
                      }
                    }}
                    state={
                      allSelected
                        ? 'checked'
                        : selected.value.size === 0
                          ? 'unchecked'
                          : 'indeterminate'
                    }
                    label={t('contacts_contacts_count-selected', {
                      totalCount: localizeValue(totalCount),
                      currentCount: allSelected
                        ? localizeValue(totalCount)
                        : localizeValue(selected.value.size),
                      count: allSelected ? totalCount : selected.value.size,
                    })}
                  />
                  {(allSelected || selected.value.size > 0) && (
                    <Button
                      type="button"
                      onClick={() => {
                        setShowDeleteModal(true);
                        track({
                          event: 'Contacts: Clicked on Delete Inactive Contacts button',
                          traits: {
                            selectedContacts: getTrackingContacts(),
                            selectedContactsCount: allSelected
                              ? totalCount
                              : getTrackingContacts().length,
                            totalContactsCount: totalCount,
                          },
                        });
                      }}
                    >
                      {t('contacts_delete')}
                    </Button>
                  )}
                </InputRow>
              )}
            </CardHeader>
            <div style={{ overflow: 'hidden' }}>
              <DataTable
                headings={headings}
                rows={rows}
                loading={loading}
                empty={<ContactListEmpty noResults={totalCount === 0} />}
                rowsToLoad={20}
                totalRows={totalCount}
                hideHeaderOnMobile
              />
            </div>
          </Card>
        </Article>
        {Boolean(currentPage) && Boolean(totalPages) && (
          <Pagination
            currentPage={currentPage}
            onChangePage={handleChangePage}
            totalPages={totalPages}
          />
        )}
      </Page>
      <Dialog
        open={showDeleteModal}
        description={t('contacts_approve_delete-contacts-message', {
          count: selected.value.size,
        })}
        confirmAction={{
          text: t('contacts_approve_delete-contacts-button', {
            count: selected.value.size,
          }),
          onClick: () => {
            removeNetworkContacts();
            track({
              event: 'Contacts: Clicked on Accept Delete Inactive Contacts button',
              traits: {
                selectedContacts: getTrackingContacts(),
                selectedContactsCount: allSelected ? totalCount : getTrackingContacts().length,
                totalContactsCount: totalCount,
              },
            });
          },
          loading: loadingRemove,
        }}
        onClose={() => {
          setShowDeleteModal(false);
          track({
            event: 'Contacts: Clicked on Cancel Delete Inactive Contacts button',
            traits: {
              selectedContacts: getTrackingContacts(),
              selectedContactsCount: allSelected ? totalCount : getTrackingContacts().length,
              totalContactsCount: totalCount,
            },
          });
        }}
      />
    </>
  );
};

export default ContactsInactivePage;
